/**
 * Keeps the React Query cache fresh from backend data-change cues — with zero
 * per-page opt-in.
 *
 * Mounted once at the app root. It reads the query cache to learn which modules
 * the user is currently looking at, keeps the WebSocket subscription set in step
 * with that, and invalidates on cue. A page enrols itself simply by running a
 * query; nothing else in the app knows this exists. That is the point: the ~8
 * hand-wired `useNotificationHandler` invalidation sites this replaces decayed
 * precisely because they were opt-in.
 *
 * Consumes the backend contract from `shared/data_freshness/` (the cue payload)
 * and `shared/notifications/connection_manager.py` (`subscribe_changes` /
 * `unsubscribe_changes`).
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useRealtimeNotifications } from '../notifications';
import { CROSS_MODULE, moduleFromQueryKey } from './moduleFromQueryKey';

/**
 * Trailing debounce on cues, per module. A single user action commonly produces
 * several cues (the write, then its side effects); one refetch pass serves them
 * all. The backend already rate-gates to one publish per tenant+module per 2s,
 * so this only has to absorb the burst a single fan-out can deliver.
 */
export const CUE_DEBOUNCE_MS = 250;

/**
 * How long the socket must stay down before falling back to polling. Short
 * blips (a deploy, a laptop lid) reconnect well inside this and never poll.
 */
export const POLL_FALLBACK_AFTER_MS = 30_000;

/** Poll interval while disconnected. Deliberately slow — this is a safety net. */
export const POLL_FALLBACK_INTERVAL_MS = 60_000;

/**
 * Recompute-subscriptions debounce. Cache events fire on every fetch, mount and
 * unmount; a page load produces dozens in a few milliseconds and they all
 * collapse into one recomputation.
 */
const CACHE_SCAN_DEBOUNCE_MS = 50;

/**
 * Modules with at least one mounted, enabled query right now.
 *
 * `CROSS_MODULE` keys are deliberately excluded: there is no `*` channel to
 * subscribe to. A cross-module query rides the subscriptions its page's other
 * queries already create — an activity feed alone on a page would go quiet, but
 * every place one is rendered also renders the module data it annotates.
 */
function activeModules(queryClient: QueryClient): Set<string> {
  const modules = new Set<string>();
  for (const query of queryClient.getQueryCache().getAll()) {
    if (!query.isActive()) continue;
    const module = moduleFromQueryKey(query.queryKey);
    if (module && module !== CROSS_MODULE) modules.add(module);
  }
  return modules;
}

interface DataFreshnessProviderProps {
  children?: React.ReactNode;
}

export function DataFreshnessProvider({ children }: DataFreshnessProviderProps) {
  const queryClient = useQueryClient();
  const { isConnected, subscribeChanges, onDataChange } = useRealtimeNotifications();

  // module -> unregister fn returned by subscribeChanges. The map IS the
  // refcount owner on this side: one entry per module, however many queries.
  const subscriptionsRef = useRef<Map<string, () => void>>(new Map());
  const cueTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // -- Auto-subscription: cache -> WS subscription set ----------------------
  useEffect(() => {
    const subscriptions = subscriptionsRef.current;
    let scanTimer: ReturnType<typeof setTimeout> | null = null;

    const reconcile = () => {
      const wanted = activeModules(queryClient);
      for (const [module, unsubscribe] of subscriptions) {
        if (!wanted.has(module)) {
          unsubscribe();
          subscriptions.delete(module);
        }
      }
      for (const module of wanted) {
        if (!subscriptions.has(module)) {
          subscriptions.set(module, subscribeChanges(module));
        }
      }
    };

    const scheduleReconcile = () => {
      if (scanTimer !== null) return;
      scanTimer = setTimeout(() => {
        scanTimer = null;
        reconcile();
      }, CACHE_SCAN_DEBOUNCE_MS);
    };

    reconcile();
    const unsubscribeCache = queryClient.getQueryCache().subscribe(scheduleReconcile);

    return () => {
      if (scanTimer !== null) clearTimeout(scanTimer);
      unsubscribeCache();
      subscriptions.forEach((unsubscribe) => unsubscribe());
      subscriptions.clear();
    };
  }, [queryClient, subscribeChanges]);

  // -- Cue -> debounced, module-scoped invalidation -------------------------
  useEffect(() => {
    const timers = cueTimersRef.current;

    const unsubscribe = onDataChange((cue) => {
      const module = cue.module;
      const pending = timers.get(module);
      if (pending) clearTimeout(pending);
      timers.set(
        module,
        setTimeout(() => {
          timers.delete(module);
          void queryClient.invalidateQueries({
            predicate: (query) => {
              const owner = moduleFromQueryKey(query.queryKey);
              return owner === module || owner === CROSS_MODULE;
            },
            // The 24h gcTime means the cache holds far more dormant queries
            // than mounted ones. Marking those stale is free; refetching them
            // would turn one cue into a burst of requests for data nobody is
            // looking at.
            refetchType: 'active',
          });
        }, CUE_DEBOUNCE_MS),
      );
    });

    return () => {
      unsubscribe();
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, [queryClient, onDataChange]);

  // -- Degradation: poll only while the socket is genuinely down ------------
  //
  // Driven by a provider-owned interval rather than a `refetchInterval` pushed
  // onto the queries. Both reach the same place — only mounted queries refetch —
  // but mutating the client's default options races React's effect order: a
  // `useQuery` re-render re-applies the options it resolved during its own
  // render phase, so a value written here can be silently reverted a moment
  // later (and, on reconnect, leave the app polling forever).
  useEffect(() => {
    if (isConnected) return;

    let poller: ReturnType<typeof setInterval> | null = null;
    const armPoller = setTimeout(() => {
      poller = setInterval(() => {
        void queryClient.refetchQueries({ type: 'active' });
      }, POLL_FALLBACK_INTERVAL_MS);
    }, POLL_FALLBACK_AFTER_MS);

    return () => {
      clearTimeout(armPoller);
      if (poller !== null) clearInterval(poller);
    };
  }, [isConnected, queryClient]);

  return <>{children}</>;
}
